import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';

export const getPurchaseInvoices = async () => {
  try {
    const response = await axiosInstance.get('PurchaseInvoice/get-purchase-invoices', {
      _suppressToast: true,
    });
    return response.data;
  } catch (error) {
    const backendMessage = error?.response?.data?.message || error?.message || '';
    const isNoDataError = /no\s*data\s*found/i.test(backendMessage);

    if (isNoDataError) {
      return { payload: [] };
    }

    throw error;
  }
};

export const getPurchaseInvoiceById = async (id) => {
  const response = await axiosInstance.get(`PurchaseInvoice/get-purchase-invoice/${id}`);
  return response.data;
};

export const addPurchaseInvoice = async (purchaseInvoice) => {
  const response = await axiosInstance.post('PurchaseInvoice/add-purchase-invoice', purchaseInvoice);
  return response.data;
};

export const updatePurchaseInvoice = async (purchaseInvoice) => {
  const response = await axiosInstance.put('PurchaseInvoice/update-purchase-invoice', purchaseInvoice);
  return response.data;
};

export const deletePurchaseInvoice = async (id) => {
  await axiosInstance.delete(`PurchaseInvoice/delete-purchase-invoice/${id}`);
};

export const getPurchaseInvoicesBySupplier = async (supplierId) => {
  const response = await axiosInstance.get(`PurchaseInvoice/by-supplier/${supplierId}`);
  return response.data;
};

/**
 * Update payment status of a purchase invoice
 * @param {Object} statusDto - UpdatePurchaseInvoiceStatusDto
 * {
 *   PurchaseInvoiceId: number,
 *   Status: "Not Paid" | "Partial Paid" | "Paid",
 *   PaidDate?: DateTime,
 *   PaymentMethod?: string,
 *   PaymentNotes?: string,
 *   Notes?: string
 * }
 */
export const updatePurchaseInvoicePaymentStatus = async (statusDto) => {
  const response = await axiosInstance.patch('PurchaseInvoice/update-status', statusDto);
  return response.data;
};

// ✅ New API methods for auto-save and draft management

/**
 * Add a line item to a draft purchase invoice
 * Allows auto-save of line items as user enters them
 */
export const addLineItem = async (purchaseInvoiceId, lineItemDto) => {
  const response = await axiosInstance.post(
    `PurchaseInvoice/${purchaseInvoiceId}/line-items`,
    lineItemDto
  );
  return response.data;
};

/**
 * Update a line item in a draft purchase invoice
 */
export const updateLineItem = async (purchaseInvoiceId, lineItemId, lineItemDto) => {
  const response = await axiosInstance.put(
    `PurchaseInvoice/${purchaseInvoiceId}/line-items/${lineItemId}`,
    lineItemDto
  );
  return response.data;
};

/**
 * Delete a line item from a draft purchase invoice
 */
export const deleteLineItem = async (purchaseInvoiceId, lineItemId) => {
  const response = await axiosInstance.delete(
    `PurchaseInvoice/${purchaseInvoiceId}/line-items/${lineItemId}`
  );
  return response.data;
};

/**
 * Post a draft purchase invoice to finalize it and update inventory
 * Changes status from Draft to Posted, updates inventory, and creates purchase history
 * Posted invoices cannot be edited
 */
export const postPurchaseInvoice = async (purchaseInvoiceId) => {
  const response = await axiosInstance.post(
    `PurchaseInvoice/${purchaseInvoiceId}/post`
  );
  return response.data;
};

/**
 * Update the document status of a purchase invoice
 * @param {Object} statusDto - { PurchaseInvoiceId: number, DocumentStatus: string }
 * NewDocumentStatus must be one of: "Draft", "Received", "VerifyQuantity", "Posted"
 * @returns {Promise<GenericApiResponse<bool>>}
 */
export const updatePurchaseDocumentStatus = async (statusDto) => {
  const response = await axiosInstance.patch(
    'PurchaseInvoice/update-document-status',
    statusDto
  );
  return response.data;
};

/**
 * Download Excel template for purchase order import
 * @returns {Promise<Blob>} Excel file blob
 */
export const downloadPurchaseOrderTemplate = async () => {
  const response = await axiosInstance.get('PurchaseInvoice/import/template', {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Validate purchase order import file
 * @param {File} file - Excel file to validate
 * @returns {Promise<Object>} Validation result with summary and issues
 */
export const validatePurchaseOrderImportFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axiosInstance.post('PurchaseInvoice/import/validate', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    timeout: 300000 // 5 minutes timeout for validation
  });
  return response.data;
};

/**
 * Confirm and import purchase order products after validation
 * @param {Object} confirmDto - Object containing ExistingProducts and NewProducts arrays from validation
 * @returns {Promise<Object>} Confirmation result with all products (existing + newly created)
 */
export const confirmPurchaseOrderImport = async (confirmDto) => {
  const response = await axiosInstance.post('PurchaseInvoice/import/confirm', confirmDto, {
    timeout: 300000 // 5 minutes timeout for import
  });
  return response.data;
};